import React, { useState, useEffect, useMemo } from "react";
import { useSearchQuery, usePagination } from "../../../hooks";
import { RoleForm } from "./form";
import { MPSharedHeader, MPSharedTable, MPSharedDeleteModal } from "../Shared/";
import { useGetRolesQuery, useDeleteRoleMutation, useGetUserQuery } from "../services";
import { getLocalizedString } from "../../../shared/translation";
import { Link } from "react-router-dom";
import { SvgIcon } from "../../Shared";
import { FMDPStorage } from "../../../shared/helper";
import { getVisibleRoleIds, getVisibleRoles } from "./roleVisibilityUtils";
import { getRoleIdsForSelectedApp } from "../../Authentication/helper";

export const RolesContainer = () => {
  const { page, setPage, perPage, pagination } = usePagination();
  const selectedAppId = FMDPStorage.get("selected-app-id");
  const currentUser = FMDPStorage.get("current-user");
  const storedRoleIds = getVisibleRoleIds(
    FMDPStorage.get("client_app_roles"),
    selectedAppId
  );
  const { data: userDetails } = useGetUserQuery(
    { id: currentUser?.id },
    { skip: !currentUser?.id }
  );

  const visibleRoleIds = useMemo(() => {
    if (!userDetails) return storedRoleIds;

    return getRoleIdsForSelectedApp(
      userDetails.roles,
      selectedAppId,
      [],
      userDetails.client_ids
    );
  }, [selectedAppId, storedRoleIds, userDetails]);

  const { setSimpleSearch, setAdvanceSearch, q } = useSearchQuery({
    page,
    setPage,
    simpleSearchKey: "name_or_client_app_app_name_cont",
  });

  const {
    data = { data: [], total_count: 0 },
    error,
    isFetching,
    isSuccess,
  } = useGetRolesQuery({
    page,
    per_page: perPage,
    order_by: "client_app_roles.created_at",
    order_dir: "desc",
    q,
  });

  const [deleteRole, { isSuccess: deleteSuccess, reset: resetDeleteRole }] =
    useDeleteRoleMutation();

  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [idToEdit, setIdToEdit] = useState(null);

  const onEdit = (datum) => {
    setShowForm(true);
    setIdToEdit(datum.id);
  };

  const onDelete = (datum) => {
    setShowDeleteWarning(true);
    setIdToDelete(datum.id);
  };

  const onCreate = () => {
    setShowForm(true);
    setIdToEdit(null);
  };

  const headers = [
    {
      label: getLocalizedString("name", "Name"),
      key: "name",
      className: "name",
    },
    {
      label: getLocalizedString("no_of_users", "No. of users"),
      nestedValue: true,
      className: "more no-of-users text-center justify-center",

      getNestedValue: ({ user_count, id, name }) => {
        return (
          <>
            {user_count > 0 ? (
              <Link
                to={`/management_portal/users_roles/users?role_id=${id}&name=${encodeURIComponent(
                  name
                )}`}
                style={{ textDecoration: "none" }}
              >
                {user_count}
              </Link>
            ) : (
              <span>0</span>
            )}
          </>
        );
      },
    },
    {
      label: getLocalizedString("system_role", "System Role"),
      key: "is_system_role",
      nestedValue: true,
      getNestedValue: ({ is_system_role }) => (
        <>
          {is_system_role ? (
            <SvgIcon name="tick" wrapperClass="status-icon active" svgClass="=icon-tick" />
          ) : (
            <SvgIcon name="cross" wrapperClass="status-icon inactive" svgClass="=icon-cross" />
          )}
        </>
      ),
      className: "w-25",
    },

    {
      label: getLocalizedString("added_on", "Added On"),
      key: "created_at",
      className: "date",
    },
    {
      label: getLocalizedString("updated_on", "Updated On"),
      key: "updated_at",
      className: "date",
      utc: true,
    },
    {
      label: getLocalizedString("updated_by", "Updated By"),
      className: "updated-by text-center",
      nestedValue: true,
      getNestedValue: ({ updated_by }) => {
        return (
          <>
            {updated_by == null ? (
              <span>NA</span>
            ) : (
              <span title={`${updated_by?.email}`}>
                {updated_by?.first_name} {updated_by?.last_name}
              </span>
            )}
          </>
        );
      },
    },
  ];

  const getDeleteItemName = (id) => {
    let name = "";
    const index = data?.data?.findIndex((item) => item.id === id);
    if (index >= 0) {
      name = data?.data[index]?.name;
    }
    return name;
  };

  const visibleRoles = useMemo(() => {
    if (!userDetails && !Array.isArray(visibleRoleIds)) return [];

    return getVisibleRoles(
      data.data || [],
      FMDPStorage.get("client_app_roles"),
      selectedAppId,
      visibleRoleIds || [],
      currentUser?.superadmin
    );
  }, [currentUser?.superadmin, data.data, selectedAppId, userDetails, visibleRoleIds]);

  useEffect(() => {
    if (deleteSuccess) {
      resetDeleteRole();
      setShowDeleteWarning(false);
    }
    return () => resetDeleteRole();
  }, [deleteSuccess]);

  return (
    <>
      <article className="main-container">
        <MPSharedHeader
          groupName="users_role"
          heading={getLocalizedString("users_and_roles", "Users And Roles")}
          resourceName="Client App Roles"
          createNewHandler={onCreate}
          handleSearchKey={(value) => setSimpleSearch(value)}
          simpleSearchPlaceholder={getLocalizedString("search_by_name_app", "Search by Name, App")}
        />

        <MPSharedTable
          resourceName="Client App Roles"
          data={visibleRoles}
          error={error}
          isLoading={isFetching}
          isSuccess={isSuccess}
          headers={headers}
          onEdit={onEdit}
          pagination={{ ...pagination, count: data.total_count }}
          skipClientId
          className="roles"
          // onDelete={onDelete}
          auditResource="ClientAppRole"
          auditKey="name"
        />
      </article>

      {showForm && <RoleForm idToEdit={idToEdit} onClose={setShowForm} />}

      <MPSharedDeleteModal
        show={showDeleteWarning}
        entityName={getLocalizedString("role", "role")}
        onHide={() => setShowDeleteWarning(false)}
        itemName={getDeleteItemName(idToDelete)}
        onDelete={() => deleteRole({ id: idToDelete })}
      />
    </>
  );
};
